import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CLOUD_PROVIDERS, DEFAULT_POD_GPU, POD_VOLUME_GB, type CloudPod, type CloudPodCloud, type RunPodCloud, type RunPodGpuOffer } from '@couch/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage } from '../../api/client';
import { CLOUD_PODS_KEY, cloudApi } from '../../api/cloud';
import { FormError, useSubmit } from '../../components/Field';
import { useDialog } from '../../components/useDialog';
import { CloudCostWarning, ConsoleLink } from './CloudBadge';
import { GcpMachineForm } from './GcpMachineForm';

const CLOUD_LABEL: Readonly<Record<CloudPodCloud, string>> = Object.freeze({ secure: 'Secure Cloud', community: 'Community Cloud', spot: 'Spot', standard: 'Standard' });
/** RunPod's price per hour; Google VMs have none of Couch's own — Google's price list applies (doc 70). */
export const perHour = (price: number | null) => (price === null ? 'price per Google\'s list' : `$${price.toFixed(2)}/h`);

function CloudChoice({ gpu, cloud, onCloud }: { gpu: RunPodGpuOffer; cloud: RunPodCloud; onCloud: (cloud: RunPodCloud) => void }) {
  return (
    <fieldset className="pod-cloud">
      <legend>Cloud</legend>
      {(['community', 'secure'] as const).map((c) => {
        const offer = gpu.clouds[c];
        return offer && (
          <label key={c}>
            <input type="radio" name="pod-cloud" value={c} checked={cloud === c} onChange={() => { onCloud(c); }} />
            {CLOUD_LABEL[c]} — {perHour(offer.pricePerHour)} · stock {offer.availability.toLowerCase()} · {offer.dataCenters.join(', ')}
          </label>
        );
      })}
      <p className="muted">Secure Cloud runs in RunPod&apos;s own datacenters; Community Cloud on vetted hosts, usually cheaper.</p>
    </fieldset>
  );
}

/** The pod's volume, or none (doc 72): with a volume Couch and the models survive a stop; without, nothing is billed while stopped. */
function VolumeField({ volume, noVolume, error, onVolume, onNoVolume }: {
  volume: string; noVolume: boolean; error: string | undefined; onVolume: (v: string) => void; onNoVolume: (v: boolean) => void;
}) {
  return (
    <div className="field">
      <label htmlFor="pod-volume">Volume disk (GB)</label>
      <input id="pod-volume" type="number" min={POD_VOLUME_GB.min} max={POD_VOLUME_GB.max} value={volume} disabled={noVolume} onChange={(e) => { onVolume(e.target.value); }} />
      <label className="checkbox">
        <input type="checkbox" checked={noVolume} onChange={(e) => { onNoVolume(e.target.checked); }} />
        No volume — use it, then delete it
      </label>
      <p className="muted">
        {noVolume
          ? 'Nothing on it can be kept: download the pictures you want, then delete the pod — after that it costs nothing. It has no Stop.'
          : 'Couch and your models live here and survive a stop. RunPod bills the volume also while the pod is stopped.'}
      </p>
      {error && <p role="alert" className="form-error">{error}</p>}
    </div>
  );
}

function PodChoices({ gpus, via, onCreated }: { gpus: RunPodGpuOffer[]; via: string; onCreated: (pod: CloudPod) => void }) {
  // Only the user's choices are state; the defaults are derived from the catalog (CLAUDE.md: never seed from loaded data).
  const [gpuChoice, setGpuChoice] = useState('');
  const [cloudChoice, setCloudChoice] = useState<RunPodCloud | ''>('');
  const [volume, setVolume] = useState(String(POD_VOLUME_GB.default));
  const [noVolume, setNoVolume] = useState(false);
  const [name, setName] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const gpu = gpus.find((g) => g.id === gpuChoice) ?? gpus.find((g) => g.id === DEFAULT_POD_GPU) ?? gpus[0];
  const cloud = cloudChoice && gpu?.clouds[cloudChoice] ? cloudChoice : gpu?.cheapest.cloud;
  const form = useSubmit(async () => {
    if (!gpu || !cloud) throw new Error('Choose a GPU.');
    const trimmed = name.trim();
    return cloudApi.createRunPodPod({ gpuTypeId: gpu.id, cloud, volumeGb: noVolume ? 0 : Number(volume), acknowledged: true, ...(trimmed ? { name: trimmed } : {}) });
  }, (result) => { onCreated(result.pod); });
  if (!gpu || !cloud) return <p role="status">RunPod has no GPU for Couch in stock in a European datacenter right now. Try again later.</p>;
  return (
    <form onSubmit={form.submit} className="pod-form">
      <div className="field">
        <label htmlFor="pod-gpu">GPU</label>
        <select id="pod-gpu" value={gpu.id} onChange={(e) => { setGpuChoice(e.target.value); }}>
          {gpus.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.memoryGb} GB · from {perHour(g.cheapest.pricePerHour)}</option>)}
        </select>
      </div>
      <CloudChoice gpu={gpu} cloud={cloud} onCloud={setCloudChoice} />
      <VolumeField volume={volume} noVolume={noVolume} error={form.fieldErrors.volumeGb} onVolume={setVolume} onNoVolume={setNoVolume} />
      <div className="field">
        <label htmlFor="pod-name">Name (optional)</label>
        <input id="pod-name" value={name} maxLength={60} placeholder={`Couch ${gpu.name.replace(/^NVIDIA (GeForce )?/, '')}`} onChange={(e) => { setName(e.target.value); }} />
        {form.fieldErrors.name && <p role="alert" className="form-error">{form.fieldErrors.name}</p>}
      </div>
      <CloudCostWarning />
      <p className="muted">Couch creates the pod with your RunPod key (via {via === 'couch' ? 'the key kept on Couch' : via}).</p>
      <label className="checkbox">
        <input type="checkbox" checked={acknowledged} onChange={(e) => { setAcknowledged(e.target.checked); }} />
        I understand: RunPod charges my account. I check RunPod&apos;s console and bill myself.
      </label>
      <FormError message={form.formError} />
      <div className="pod-actions">
        <button type="submit" disabled={!acknowledged || form.pending}>{form.pending ? 'Creating…' : `Create pod · ${perHour(gpu.clouds[cloud]?.pricePerHour ?? 0)}`}</button>
        <ConsoleLink href={CLOUD_PROVIDERS.runpod.console} provider="runpod" />
      </div>
    </form>
  );
}

function PodForm({ onCreated }: { onCreated: (pod: CloudPod) => void }) {
  const catalog = useQuery({ queryKey: ['runpod-catalog'], queryFn: cloudApi.runpodCatalog, retry: false, staleTime: 60_000 });
  if (catalog.isPending) return <p role="status">Reading RunPod&apos;s prices and stock…</p>;
  if (catalog.isError) {
    const noKey = catalog.error instanceof ApiError && catalog.error.status === 409;
    return (
      <div role="alert" className="form-error">
        <p>{errorMessage(catalog.error)}</p>
        {noKey && <p><Link to="/connections">Connections → RunPod</Link></p>}
      </div>
    );
  }
  return <PodChoices gpus={catalog.data.gpus} via={catalog.data.via} onCreated={onCreated} />;
}

/** Where it stands: Google still looking for a zone, failed, installing, or never connected (docs 58, 70). */
function ProgressNote({ pod }: { pod: CloudPod }) {
  const vm = pod.provider === 'gcp';
  if (pod.state === 'creating') return <p role="status">Google is looking for an L4 in a European zone — this takes up to a few minutes. You can close this window.</p>;
  if (pod.state === 'failed') return <p role="alert" className="form-error">{pod.error ?? 'No VM was created.'}</p>;
  if (pod.state === 'expired') {
    return (
      <p role="alert" className="form-error">
        {vm
          ? <>The VM did not connect within an hour. Its log: <code>sudo journalctl -u google-startup-scripts</code> (Google console → SSH); delete it if you do not need it.</>
          : <>The pod did not connect within an hour. Its log is <code>/workspace/couch-start.log</code> (RunPod → Connect → Web terminal); terminate the pod in RunPod&apos;s console if you do not need it.</>}
      </p>
    );
  }
  return <p role="status">Couch installs itself on the {vm ? 'VM' : 'pod'} — it shows up under My machines in about 5–15 minutes. You can close this window.</p>;
}

function PodProgress({ pod, onPaired }: { pod: CloudPod; onPaired: () => void }) {
  const pods = useQuery({ queryKey: CLOUD_PODS_KEY, queryFn: cloudApi.cloudPods, refetchInterval: 5000 });
  const current = pods.data?.pods.find((p) => p.id === pod.id) ?? pod;
  useEffect(() => { if (current.state === 'paired') onPaired(); }, [current.state, onPaired]);
  const where = current.dataCenterIds.length > 0 ? `, ${current.dataCenterIds.join(', ')}` : '';
  return (
    <div className="pod-progress">
      <p>{pod.provider === 'gcp' ? 'VM' : 'Pod'} <code>{pod.podId}</code>: {pod.gpuName}, {CLOUD_LABEL[pod.cloud]}, {perHour(pod.pricePerHour)}{where}.</p>
      <ProgressNote pod={current} />
      <ConsoleLink href={current.consoleUrl} provider={pod.provider} />
      <CloudCostWarning />
    </div>
  );
}

type Provider = 'runpod' | 'gcp';

function ProviderChoice({ provider, onProvider }: { provider: Provider; onProvider: (p: Provider) => void }) {
  return (
    <fieldset className="pod-cloud">
      <legend>Provider</legend>
      {(['runpod', 'gcp'] as const).map((p) => (
        <label key={p}>
          <input type="radio" name="cloud-provider" value={p} checked={provider === p} onChange={() => { onProvider(p); }} />
          {CLOUD_PROVIDERS[p].label}
        </label>
      ))}
    </fieldset>
  );
}

/** "New cloud machine" (docs 58, 70): a RunPod pod or a Google Cloud VM that installs Couch by itself. */
export function NewCloudMachineDialog({ onClose, onPaired }: { onClose: () => void; onPaired: () => void }) {
  const [created, setCreated] = useState<CloudPod | null>(null);
  const [provider, setProvider] = useState<Provider>('runpod');
  const queryClient = useQueryClient();
  const ref = useDialog(onClose);
  // My machines lists the pod at once, also when the dialog is closed early.
  const onCreated = (pod: CloudPod) => { setCreated(pod); void queryClient.invalidateQueries({ queryKey: CLOUD_PODS_KEY }); };
  return (
    <div className="overlay">
      <div ref={ref} className="dialog" role="dialog" aria-modal="true" aria-labelledby="new-cloud-title">
        <h2 id="new-cloud-title">New cloud machine</h2>
        {created ? <PodProgress pod={created} onPaired={onPaired} /> : (
          <>
            <ProviderChoice provider={provider} onProvider={setProvider} />
            {provider === 'gcp' ? <GcpMachineForm onCreated={onCreated} /> : <PodForm onCreated={onCreated} />}
          </>
        )}
        <button type="button" className="button-quiet dialog-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
